/** @file Tool-mode finite state machine + drawing-in-progress tracking. */
import { appStore } from "../core/state.js";
import { execute } from "../core/history.js";
import {
  addDrawingCommand,
  deleteDrawingCommand,
  selectDrawingCommand,
  setToolCommand,
} from "../core/commands.js";

/** @typedef {import("../core/play.js").ToolMode} ToolMode */
/** @typedef {{x:number,y:number}} Point */

const MIN_DRAW_LENGTH_M = 0.5;

/**
 * @typedef {object} DraftDrawing
 * @property {"run" | "pass" | "shot"} type
 * @property {string} color
 * @property {Point} start
 * @property {Point} current
 */

/** @type {DraftDrawing | null} */
let draft = null;
/** @type {Set<(d: DraftDrawing | null) => void>} */
const draftListeners = new Set();

/**
 * Change tool mode via command.
 * @param {ToolMode} next
 */
export function setTool(next) {
  const current = appStore.get().tool;
  if (current === next) {
    return;
  }
  execute(setToolCommand(current, next));
  draft = null;
  notifyDraft();
}

/**
 * Handle a pointerdown in meter-space coords.
 * @param {Point} meters
 * @param {{ isToken: boolean, isDrawing: boolean, drawingId?: string }} hit
 */
export function onPointerDown(meters, hit) {
  const state = appStore.get();
  const tool = state.tool;

  if (tool === "erase") {
    if (hit.isDrawing && hit.drawingId) {
      const cmd = deleteDrawingCommand(hit.drawingId);
      if (cmd) {
        execute(cmd);
      }
    }
    return;
  }

  if (tool === "select") {
    if (hit.isDrawing && hit.drawingId) {
      if (state.selectedDrawingId !== hit.drawingId) {
        execute(selectDrawingCommand(state.selectedDrawingId, hit.drawingId));
      }
      return;
    }
    if (!hit.isToken && state.selectedDrawingId) {
      execute(selectDrawingCommand(state.selectedDrawingId, null));
    }
    return;
  }

  // Drawing tools (run | pass | shot)
  if (!hit.isToken && !hit.isDrawing) {
    draft = {
      type: /** @type {"run"|"pass"|"shot"} */ (tool),
      color: state.drawColor,
      start: { x: meters.x, y: meters.y },
      current: { x: meters.x, y: meters.y },
    };
    notifyDraft();
  }
}

/**
 * @param {Point} meters
 */
export function onPointerMove(meters) {
  if (!draft) {
    return;
  }
  draft = { ...draft, current: { x: meters.x, y: meters.y } };
  notifyDraft();
}

export function onPointerUp() {
  if (!draft) {
    return;
  }
  const dx = draft.current.x - draft.start.x;
  const dy = draft.current.y - draft.start.y;
  const len = Math.hypot(dx, dy);
  if (len >= MIN_DRAW_LENGTH_M) {
    execute(
      addDrawingCommand({
        type: draft.type,
        color: draft.color,
        points: [
          { x: draft.start.x, y: draft.start.y },
          { x: draft.current.x, y: draft.current.y },
        ],
      }),
    );
  }
  draft = null;
  notifyDraft();
}

export function cancelDraft() {
  if (!draft) {
    return;
  }
  draft = null;
  notifyDraft();
}

/** @returns {DraftDrawing | null} */
export function getDraft() {
  return draft;
}

/**
 * @param {(d: DraftDrawing | null) => void} fn
 * @returns {() => void}
 */
export function onDraftChange(fn) {
  draftListeners.add(fn);
  return () => draftListeners.delete(fn);
}

function notifyDraft() {
  for (const fn of draftListeners) {
    fn(draft);
  }
}

/** Test helper. */
export function __reset() {
  draft = null;
  draftListeners.clear();
}
